import { useMemo } from 'react';
import type { MetadataSchemaField } from '@/types/metadata.types';
import { useOptionSourceOptions } from '@/api/hooks/useOptionSources';

export interface ResolvedOption {
  value: string;
  label: string;
}

export interface ResolvedFieldOptions {
  options: ResolvedOption[];
  isLoading: boolean;
}

/**
 * Resolve the selectable options for a DROPDOWN/MULTISELECT field, transparently
 * supporting both modes:
 *  - MANUAL (default): the field's hand-typed, active `options`.
 *  - DATASOURCE: options fetched live from the registered source identified by
 *    `optionsSourceKey` (e.g. the Manufacturer provider catalog).
 *
 * Hook usage is unconditional (the data-source query is simply disabled in
 * MANUAL mode) so this is safe to call from any field renderer.
 */
export function useResolvedFieldOptions(field: MetadataSchemaField): ResolvedFieldOptions {
  const isDataSource = field.optionsSourceType === 'DATASOURCE' && !!field.optionsSourceKey;

  const { data: sourceOptions, isLoading } = useOptionSourceOptions(
    field.optionsSourceKey,
    isDataSource
  );

  return useMemo(() => {
    if (isDataSource) {
      return {
        options: (sourceOptions ?? []).map((o) => ({ value: o.value, label: o.label })),
        isLoading,
      };
    }
    return {
      options: (field.options ?? [])
        .filter((opt) => opt.isActive)
        .map((opt) => ({ value: opt.optionValue, label: opt.optionLabel })),
      isLoading: false,
    };
  }, [isDataSource, sourceOptions, isLoading, field.options]);
}
